import React, { useState, useEffect } from 'react';

import { Box, Button, CircularProgress, IconButton, Typography } from '@material-ui/core';
import { ChevronLeft, ChevronRight, Sync } from '@mui/icons-material';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

const ObservationChart = ({ data, loading, error, onRefresh }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(5);

  useEffect(() => {
    setCurrentPage(0);
  }, [data]);

  const calculatePagination = (graphData) => {
    const effectiveItemsPerPage = Math.round(30 - zoomLevel * 2.4);

    const totalPages = Math.max(1, Math.ceil(graphData.length / effectiveItemsPerPage));
    const startIndex = currentPage * effectiveItemsPerPage;
    const endIndex = Math.min(startIndex + effectiveItemsPerPage, graphData.length);
    const pageData = graphData.slice(startIndex, endIndex);

    // Calculate the minimum zoom level that would result in a single page
    const minZoomForSinglePage = Math.ceil((30 - Math.ceil(graphData.length)) / 2.4);

    const defaultItemsPerPage = Math.round(30 - 5 * 2.4);
    const fitsOnOnePageByDefault = graphData.length <= defaultItemsPerPage;

    return { fitsOnOnePageByDefault, minZoomForSinglePage, pageData, totalPages };
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="300px">
        <CircularProgress />
      </Box>
    );
  }

  // Handle null, undefined, or empty data cases
  if (!data || error || (Array.isArray(data) && data.length === 0)) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="300px" flexDirection="column">
        <Typography variant="body1" color="error" gutterBottom>
          {error || 'No data available for the selected filters'}
        </Typography>
        <Button variant="outlined" color="primary" startIcon={<Sync />} onClick={onRefresh} style={{ marginTop: 16 }}>
          Refresh
        </Button>
      </Box>
    );
  }

  const { pageData, totalPages, fitsOnOnePageByDefault } = calculatePagination(data);

  const pageDataReversed = [...pageData].reverse();

  const handlePrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  return (
    <>
      <Box display="flex" justifyContent="flex-end" gridGap={24} alignItems="center" mt={2} mb={1}>
        {!fitsOnOnePageByDefault && (
          <div style={{ alignItems: 'center', backgroundColor: '#f5f5f5', borderRadius: '20px', display: 'flex', padding: '4px', width: '120px' }}>
            <IconButton
              color="primary"
              onClick={() => {
                // Get the minimum zoom level that would result in a single page
                const { minZoomForSinglePage } = calculatePagination(data);
                const minZoom = Math.max(1, minZoomForSinglePage);
                const newZoom = Math.max(minZoom, zoomLevel - 1);
                setZoomLevel(newZoom);
                // Reset to first page when changing zoom
                setCurrentPage(0);
              }}
              disabled={zoomLevel <= 1 || (data && zoomLevel <= calculatePagination(data).minZoomForSinglePage)}
              size="small"
              style={{
                backgroundColor: zoomLevel <= 1 || (data && zoomLevel <= calculatePagination(data).minZoomForSinglePage) ? '#f5f5f5' : '#fff',
                borderRadius: '50%',
                padding: '4px 8px',
              }}>
              <span style={{ fontSize: '16px', fontWeight: 'bold' }}>-</span>
            </IconButton>

            <Typography variant="body2" style={{ flex: 1, margin: '0 10px', textAlign: 'center' }}>
              Zoom
            </Typography>

            <IconButton
              color="primary"
              onClick={() => {
                const newZoom = Math.min(10, zoomLevel + 1);

                setZoomLevel(newZoom);
                setCurrentPage(0);
              }}
              disabled={zoomLevel >= 10}
              size="small"
              style={{
                backgroundColor: zoomLevel >= 10 ? '#f5f5f5' : '#fff',
                borderRadius: '50%',
                padding: '4px 8px',
              }}>
              <span style={{ fontSize: '16px', fontWeight: 'bold' }}>+</span>
            </IconButton>
          </div>
        )}

        {totalPages > 1 && (
          <div style={{ alignItems: 'center', backgroundColor: '#f5f5f5', borderRadius: '20px', display: 'flex', padding: '4px' }}>
            <IconButton
              color="primary"
              onClick={handleNextPage}
              disabled={currentPage === totalPages - 1}
              size="small"
              style={{ backgroundColor: currentPage === totalPages - 1 ? '#f5f5f5' : '#fff', borderRadius: '50%' }}>
              <ChevronLeft />
            </IconButton>

            <Typography variant="body2" style={{ margin: '0 10px' }}>
              {totalPages - currentPage}/{totalPages}
            </Typography>

            <IconButton
              color="primary"
              onClick={handlePrevPage}
              disabled={currentPage === 0}
              size="small"
              style={{ backgroundColor: currentPage === 0 ? '#f5f5f5' : '#fff', borderRadius: '50%' }}>
              <ChevronRight />
            </IconButton>
          </div>
        )}
      </Box>

      <ResponsiveContainer width="100%" height={400}>
        <BarChart data={pageDataReversed} margin={{ bottom: 20, left: 30, right: 20, top: 20 }} barGap={1} barCategoryGap={Math.max(2, zoomLevel * 1.2)}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" vertical={false} strokeOpacity={0.4} />

          <XAxis
            dataKey="date"
            tickLine={false}
            axisLine={{ stroke: '#E0E0E0' }}
            interval={0}
            fontSize={Math.min(16, 9 + zoomLevel)}
            angle={Math.round(30 - zoomLevel * 2.4) > 14 ? -45 : 0}
            textAnchor={Math.round(30 - zoomLevel * 2.4) > 14 ? 'end' : 'middle'}
            height={Math.round(30 - zoomLevel * 2.4) > 14 ? 80 : 30}
          />
          <YAxis
            label={{ angle: -90, position: 'insideLeft', style: { fontFamily: 'Roboto', fontSize: '14px', textAnchor: 'middle' }, value: 'Tech QA Cases' }}
            tickLine={false}
            axisLine={{ display: 'none' }}
          />

          <Tooltip cursor={{ fill: 'rgba(0, 0, 0, 0.05)' }} />

          <Legend align="center" verticalAlign="bottom" wrapperStyle={{ padding: '10px' }} />

          <Bar dataKey="routine" fill="#2AAAE2" name="Routine" barSize={Math.max(6, 6 + zoomLevel)} radius={[3, 3, 3, 3]} />
          <Bar dataKey="callback" fill="#999999" name="Call Back" barSize={Math.max(6, 6 + zoomLevel)} radius={[3, 3, 3, 3]} />
          <Bar dataKey="stat" fill="orange" name="STAT" barSize={Math.max(6, 6 + zoomLevel)} radius={[3, 3, 3, 3]} />
        </BarChart>
      </ResponsiveContainer>
    </>
  );
};

export default ObservationChart;
